"use client"
import { Content, isFilled } from "@prismicio/client"
import { SliceComponentProps } from "@prismicio/react"
import { PrismicNextImage } from "@prismicio/next"
import {
  useForm,
  useStatus,
  useFormIsValid,
  useFormValues,
} from "@aura-design/system/form"
import {
  ALL_SURFACES,
  ALL_VISIBILITIES,
  ALL_WEATHERS,
  ALL_FRECUENCY,
  ALL_AIRPORTS,
} from "@airplan/utils/constants"
import { hazardReportForm } from "@airplan/utils/validator-schema"
import { sendContactForm } from "@/utils/contact"
import { onFileUploadChange, uploadFile } from "@airplan/utils/files-form"
import Input from "@aura-design/system/input"
import Select from "@airplan/ui/select"
import Grid from "@aura-design/system/grid"
import Button from "@aura-design/system/button"
import Alert from "@aura-design/system/alert"
import Textarea from "@aura-design/system/textarea"

/**
 * Props for `FormHazardReport`.
 */
export type FormHazardReportProps =
  SliceComponentProps<Content.FormHazardReportSlice>

/**
 * Component for "FormHazardReport" Slices.
 */
const FormHazardReport = ({ slice }: FormHazardReportProps): JSX.Element => {
  const formData = useForm({
    airport: "",
    date: "",
    time: "",
    weather: "",
    location: "",
    message: "",
    reason: "",
    suggestions: "",
    eventFrecuency: "",
    fullName: "",
    email: "",
    phone: "",
    company: "",
    surface: "",
    visibility: "",
    accept: false,
    file: "",
  })

  const status = useStatus()

  const {
    airport,
    date,
    time,
    weather,
    location,
    message,
    reason,
    suggestions,
    eventFrecuency,
    fullName,
    email,
    phone,
    company,
    file,
    accept,
    surface,
    visibility,
  } = formData

  const isValid = useFormIsValid(formData, hazardReportForm)

  const handleContactForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    status.resetStatus()
    status.setIsLoading(true)
    if (!isValid) {
      for (const field in formData) {
        formData[field]?.setTouch(true)
      }
      status.resetStatus()
      status.setIsLoading(false)
      status.setMessage("Por favor revisa que todos los datos sean correctos")
      return
    }
    let publicUrl
    if (file?.value) {
      const resFile = await uploadFile(file.value)
      if (resFile.error) {
        status.setIsLoading(false)
        status.setIsError(true)
        status.setMessage(resFile.error)
        return
      }
      publicUrl = resFile.publicUrl
    }
    const formValues = useFormValues(formData)
    const res = await sendContactForm(
      { ...formValues, publicUrl },
      "formHazardReport",
    )
    handleResponse(res)
  }

  const handleResponse = (res: any) => {
    if (res.status !== 200) {
      status.setIsError(true)
      status.setMessage("Ha ocurrido un error al enviar el correo electrónico")
      return
    }
    for (const field in formData) {
      formData[field]?.reset()
      formData[field]?.setTouch(false)
    }
    accept?.setValue(false)
    file?.setValue("")
    status.setMessage("Tu mensaje ha sido enviado")
    status.setSubmited(true)
    status.setIsLoading(false)
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="hazard-report-form bg-dark-blue text-white"
    >
      <Grid col="twelve">
        <div className="span-9">
          <form onSubmit={handleContactForm} className="pad smush">
            <Grid col="two">
              <div>
                <label>Aeropuerto*</label>
                <Select placeholder="Selecciona" {...airport}>
                  {ALL_AIRPORTS.filter((item) => item.value != "ALL").map(
                    (airport) => (
                      <option key={airport.value} value={airport.value}>
                        {airport.text}
                      </option>
                    ),
                  )}
                </Select>
              </div>
              <div>
                <label>Fecha del evento*</label>
                <Input type="date" {...date} />
              </div>
              <div>
                <label>Hora del evento*</label>
                <Input type="time" {...time} />
              </div>
              <div>
                <label>Clima*</label>
                <Select placeholder="Selecciona" {...weather}>
                  {ALL_WEATHERS.map((weather) => (
                    <option key={weather.value} value={weather.value}>
                      {weather.text}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="span-2">
                <label>
                  Localización (ej. pista, calle de rodaje Alpha, posición No.
                  2, etc.)*
                </label>
                <Input {...location} />
              </div>
              <div className="span-2">
                <label>Descripción de lo ocurrido (¿qué pasó?)*</label>
                <Textarea {...message} />
              </div>
              <div>
                <label>Visibilidad*</label>
                <Select placeholder="Selecciona" {...visibility}>
                  {ALL_VISIBILITIES.map((visibility) => (
                    <option key={visibility.value} value={visibility.value}>
                      {visibility.text}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label>Estado de la superficie*</label>
                <Select placeholder="Selecciona" {...surface}>
                  {ALL_SURFACES.map((surface) => (
                    <option key={surface.value} value={surface.value}>
                      {surface.text}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="span-2">
                <label>Factores causales (¿qué causó la situación?)*</label>
                <Input placeholder="Selecciona" {...reason} />
              </div>
              <div className="span-2">
                <label>
                  Sugerencias de acciones correctivas (¿qué medidas podrían
                  prevenir que esto ocurra nuevamente?)
                </label>
                <Input {...suggestions} />
              </div>
              <div className="span-2">
                <label>
                  Frecuencia del evento (¿cuántas veces has presenciado o
                  conocido de esta situación?)*
                </label>
                <Select {...eventFrecuency} placeholder="Selecciona">
                  {ALL_FRECUENCY.map((frecuency) => (
                    <option key={frecuency.value} value={frecuency.value}>
                      {frecuency.text}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label>Nombre de quien reporta (opcional)</label>
                <Input {...fullName} />
              </div>
              <div>
                <label>Correo de quien reporta (opcional)</label>
                <Input {...email} />
              </div>
              <div>
                <label>Teléfono de quien reporta (opcional)</label>
                <Input {...phone} />
              </div>
              <div>
                <label>Compañía/cargo de quien reporta (opcional)</label>
                <Input {...company} />
              </div>
              <div className="span-2">
                <em className="text-xs">Tamaño máximo: 20 MB</em>
                <Input
                  type="file"
                  className="default"
                  {...file}
                  onChange={(event) => onFileUploadChange(event, file)}
                />
              </div>
              <div className="span-2">
                <label className="flex items-center inputer-m-0 my-2">
                  <Input
                    className="default m-0 mr-1"
                    type="checkbox"
                    name="accept"
                    {...accept}
                    checked={!!accept?.value}
                    onChange={(e) => accept?.setValue(e.target.checked)}
                  />
                  Acepto la Política de Tratamiento de Datos Personales*
                </label>
              </div>
              <div className="span-2">
                <Alert state={status.state} isPushBottom />
              </div>
              <div className="span-2 text-center">
                <Button
                  type="submit"
                  className="hover:border-dark-yellow` border-white bg-white text-black"
                  isLoading={status.state.isLoading}
                  isLoadingText="Enviando..."
                  label="Enviar"
                />
              </div>
            </Grid>
          </form>
        </div>
        <div className="span-3 relative">
          <div className="sticky py-3">
            <div className="relative h-20 md:h-[100vh]">
              {isFilled.image(slice.primary.image) && (
                <PrismicNextImage
                  field={slice.primary.image}
                  className="rounded-xl object-cover"
                  imgixParams={{
                    txt: slice.primary.image?.copyright,
                  }}
                  fill
                  alt=""
                />
              )}
            </div>
          </div>
        </div>
      </Grid>
    </section>
  )
}

export default FormHazardReport
